import { useCallback, useEffect, useRef, useState } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import cc from "classcat"
import {
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline"
import H2 from "components/atoms/H2"
import LocalizedLink from "components/LocalizedLink"
import { CardHover } from "components/atoms/common/CardHover"
import useTranslations from "hooks/useTranslations"
import usePrefersReducedMotion from "hooks/usePrefersReducedMotion"

// Cards are 40px apart (gap-10); the arrows step by one card plus this gap.
const GAP_PX = 40

// Posts carrying this tag in their frontmatter are the ones shown here, and
// "Read all blog posts" opens the blog index filtered to the same tag. To add
// or remove a post, edit its `tags` line; no code change needed.
const PARENTS_TAG = "parents"

/**
 * en-us posts tagged "parents" from the MDX blog (src/markdown-pages/blog),
 * the same source the /blog index uses. organisms/common/BlogPosts reads Prismic
 * instead, which only holds a handful of en-us posts, mostly from 2022.
 *
 * Locale and tag are literals in the query because this page only exists on
 * the global build, and static queries can't take variables; keep the tag in
 * the query in step with PARENTS_TAG.
 *
 * A horizontal carousel: three cards in view on desktop, the rest reached with
 * the arrows beside the heading, or by swiping on touch. Built on native
 * scroll-snap rather than react-slick so swiping, keyboard focus (tabbing to a
 * card scrolls it into view) and search engines all see a plain list of links.
 */
export default function ParentInfoBlog() {
  const { parentInfoBlogTitle, parentInfoBlogAllLink } = useTranslations()
  const data = useStaticQuery(graphql`
    query ParentInfoBlogPosts {
      allMdx(
        sort: { frontmatter: { date: DESC } }
        filter: {
          frontmatter: {
            hidden: { ne: true }
            type: { eq: "blog" }
            tags: { in: ["parents"] }
          }
          fields: { locale: { eq: "en-us" } }
        }
      ) {
        nodes {
          id
          frontmatter {
            date(formatString: "MMMM DD, YYYY")
            title
            slug
            external_url
            image {
              childImageSharp {
                gatsbyImageData(width: 600, aspectRatio: 1.7778)
              }
            }
          }
        }
      }
    }
  `)

  const listRef = useRef<HTMLUListElement>(null)
  const [canPrev, setCanPrev] = useState(false)
  const [canNext, setCanNext] = useState(false)
  const prefersReducedMotion = usePrefersReducedMotion()

  // Arrows disable at either end. A couple of pixels of slack, because
  // fractional card widths leave scrollLeft just short of the true maximum.
  const updateArrows = useCallback(() => {
    const list = listRef.current
    if (!list) return
    setCanPrev(list.scrollLeft > 2)
    setCanNext(list.scrollLeft + list.clientWidth < list.scrollWidth - 2)
  }, [])

  useEffect(() => {
    updateArrows()
    window.addEventListener("resize", updateArrows)
    return () => window.removeEventListener("resize", updateArrows)
  }, [updateArrows])

  const scrollByCard = (direction: 1 | -1) => {
    const list = listRef.current
    const card = list?.firstElementChild as HTMLElement | null
    if (!list || !card) return
    list.scrollBy({
      left: direction * (card.offsetWidth + GAP_PX),
      behavior: prefersReducedMotion ? "auto" : "smooth",
    })
  }

  const posts = data.allMdx.nodes
  if (!posts.length) return null

  const arrowClass =
    "inline-flex h-10 w-10 items-center justify-center rounded-full bg-kkm-green text-zinc-900 transition-[background-color,opacity,transform] duration-150 ease-out hover:bg-white active:scale-[0.96] disabled:cursor-default disabled:opacity-30 disabled:hover:bg-kkm-green focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-800"

  return (
    <div className="bg-zinc-800 py-16 lg:py-24">
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Arrows are 40px, no taller than one line of the heading, and
            centred on it, so they never add height above or below the title.
            `!mb-0` because H2 brings its own mb-2, which an m-0 can't beat. */}
        <div className="mb-8 flex items-center justify-between gap-6">
          <H2
            size="lg"
            className="m-0 !mb-0 text-left font-extrabold tracking-tight"
            theme="dark"
          >
            {parentInfoBlogTitle}
          </H2>
          <div className="flex shrink-0 gap-3">
            <button
              type="button"
              aria-label="Previous blog posts"
              onClick={() => scrollByCard(-1)}
              disabled={!canPrev}
              className={arrowClass}
            >
              <ChevronLeftIcon aria-hidden="true" className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label="Next blog posts"
              onClick={() => scrollByCard(1)}
              disabled={!canNext}
              className={arrowClass}
            >
              <ChevronRightIcon aria-hidden="true" className="h-5 w-5" />
            </button>
          </div>
        </div>
        <ul
          ref={listRef}
          onScroll={updateArrows}
          className={cc([
            "m-0 flex snap-x snap-mandatory list-none gap-10 overflow-x-auto p-0",
            // A scroll container clips on both axes, which would cut off
            // CardHover's 8px lift and its shadow. Padding gives them room and
            // the matching negative margin keeps the cards where they were.
            "-my-6 py-6",
            // The arrows replace the scrollbar; swiping still works on touch.
            "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          ])}
        >
          {posts.map(({ id, frontmatter }) => {
            const card = (
              <>
                <CardHover className="rounded-lg">
                  <GatsbyImage
                    image={getImage(frontmatter.image)}
                    className="aspect-video w-full rounded-lg"
                    imgStyle={{ objectFit: "cover", borderRadius: "0.5rem" }}
                    alt=""
                  />
                </CardHover>
                <p className="m-0 mt-4 font-display text-2xl font-semibold text-white">
                  {frontmatter.title}
                </p>
                <time className="mt-2 block text-base text-zinc-300">
                  {frontmatter.date}
                </time>
              </>
            )
            return (
              // One card and a peek of the next on phones, two on tablets,
              // three on desktop; widths subtract the gaps between them.
              <li
                key={id}
                className="w-[85%] shrink-0 snap-start sm:w-[calc((100%-2.5rem)/2)] md:w-[calc((100%-5rem)/3)]"
              >
                {frontmatter.external_url ? (
                  <a
                    href={frontmatter.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    {card}
                  </a>
                ) : (
                  <LocalizedLink
                    to={`/blog/${frontmatter.slug}`}
                    className="block"
                  >
                    {card}
                  </LocalizedLink>
                )}
              </li>
            )
          })}
        </ul>
        {/* Below the row rather than beside the arrows, where it would
            squeeze the heading on phones. */}
        <LocalizedLink
          to={`/blog/tags/${PARENTS_TAG}`}
          className="group mt-8 inline-flex items-center gap-2 font-display text-lg font-semibold text-kkm-green hover:text-white"
        >
          {parentInfoBlogAllLink}
          <ArrowRightIcon
            aria-hidden="true"
            className="h-5 w-5 transition-transform duration-150 group-hover:translate-x-1"
          />
        </LocalizedLink>
      </section>
    </div>
  )
}
